import React, { useState, useRef, useEffect } from "react";
import { Layout, Menu, Drawer, Grid } from "antd";
import {
  AppstoreOutlined,
  UserOutlined,
  DatabaseOutlined,
  BellOutlined,
  SettingOutlined,
  FileTextOutlined,
  BarChartOutlined,
  CreditCardOutlined,
  PieChartOutlined,
} from "@ant-design/icons";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import DropDownProfile from "src/components/reusable/DropDownProfile";

const menuConfig: Record<
  string,
  { label: string; icon: React.ReactNode; path: string }
> = {
  "1": {
    label: "Dashboard",
    icon: <BarChartOutlined style={{ fontSize: "18px" }} />,
    path: "dashboard",
  },
  "2": {
    label: "Pipeline Health",
    icon: <DatabaseOutlined style={{ fontSize: "18px" }} />,
    path: "pipeline-health",
  },
  "3": {
    label: "Users",
    icon: <UserOutlined style={{ fontSize: "18px" }} />,
    path: "users",
  },
  "4": {
    label: "Data Sources",
    icon: <AppstoreOutlined style={{ fontSize: "18px" }} />,
    path: "data-sources",
  },
  "5": {
    label: "Subscriptions",
    icon: <CreditCardOutlined style={{ fontSize: "18px" }} />,
    path: "subscriptions",
  },
  "6": {
    label: "Activity Logs",
    icon: <FileTextOutlined style={{ fontSize: "18px" }} />,
    path: "activity-logs",
  },
  "7": {
    label: "Announcements",
    icon: <BellOutlined style={{ fontSize: "18px" }} />,
    path: "announcements",
  },
  "8": {
    label: "Settings",
    icon: <SettingOutlined style={{ fontSize: "18px" }} />,
    path: "settings",
  },
  "9": {
    label: "Quota Management", // ✅ New menu item
    icon: <PieChartOutlined style={{ fontSize: "18px" }} />,
    path: "quota-management", // ✅ route
  },
};

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("1");
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { Header, Sider, Content } = Layout;
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Optional: Automatically select menu based on URL
  useEffect(() => {
    const pathMap: Record<string, string> = {
      "/admin-portal/dashboard": "1",
      "/admin-portal/pipeline-health": "2",
      "/admin-portal/users": "3",
      "/admin-portal/data-sources": "4",
      "/admin-portal/subscriptions": "5",
      "/admin-portal/activity-logs": "6",
      "/admin-portal/announcements": "7",
      "/admin-portal/settings": "8",
      "/admin-portal/usage-and-limits": "9", // ✅ Added
    };
    const key = pathMap[location.pathname];
    if (key) {
      setSelectedMenuItem(key);
    }
  }, [location.pathname]);

  // Collapse on click *outside* the sidebar
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const sidebarEl = sidebarRef.current;
      if (
        sidebarEl &&
        e.target instanceof Node &&
        !sidebarEl.contains(e.target)
      ) {
        setCollapsed(true);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const currentMenu = menuConfig[selectedMenuItem];
  const colorBgContainer = "#fff";

  const renderSidebarContent = () => (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="35"
        height="32"
        className="dt"
        style={{ margin: "5px auto", marginTop: "20px", display: "block" }}
        viewBox="0 0 35 32"
        fill="none"
        onClick={e => {
          e.stopPropagation();
          setCollapsed(prev => !prev);
        }}
      >
        <path
          d="M1 30.5C12.2113 27.974 5.52192 2.0716 17 1.5C28.5918 0.922731 22.2352 27.7056 33.5 30.5"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>

      <hr style={{ margin: "11px auto", height: "1px", opacity: "0.3" }} />

      <Menu
        theme="dark"
        mode="inline"
        defaultSelectedKeys={["1"]}
        selectedKeys={[selectedMenuItem]}
        style={{ backgroundColor: "#2B68AD", color: "white" }}
      >
        {Object.entries(menuConfig).map(([key, { label, icon, path }]) => (
          <Menu.Item
            key={key}
            icon={icon}
            style={{ color: "white" }}
            onClick={e => {
              e.domEvent.stopPropagation();
              setCollapsed(true);
              setSelectedMenuItem(key);
              navigate(`/admin-portal/${path}`);
            }}
          >
            {label}
          </Menu.Item>
        ))}
      </Menu>
    </>
  );

  return (
    <Layout  className="admin-portal-layout" style={{ minHeight: "100vh" }}>
      <button
        className="hamburger-btn"
        onClick={() => setDrawerVisible(true)}
        aria-label="Open menu"
      >
        ☰
      </button>
      {isMobile ? (
        <Drawer
          placement="left"
          closable={false}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          styles={{ body: { padding: 0, backgroundColor: "#2B68AD" } }}
          width={220}
        >
          {renderSidebarContent()}
        </Drawer>
      ) : (
        <Sider
          ref={sidebarRef}
          trigger={null}
          collapsible
          collapsed={collapsed}
          onClick={e => {
            const target = e.target as HTMLElement;
            const clickedInsideMenu = target.closest(".ant-menu-item");
            const clickedOnLogo = target.closest("svg");
            if (!clickedInsideMenu && !clickedOnLogo) {
              setCollapsed(prev => !prev);
            }
          }}
          style={{
            backgroundColor: "#2B68AD",
            position: "fixed",
            height: "100%",
            zIndex: 1000,
          }}
        >
          {renderSidebarContent()}
        </Sider>
      )}

      <Layout className={`${collapsed ? "responsive-margin" : "header-style"}`}>
        <Header
          className="header-dashboard d-flex justify-content-between"
          style={{ background: colorBgContainer }}
        >
          <div className="headline">
            {currentMenu && (
              <div className="menu-heading-wrapper d-flex align-items-center gap-2">
                <img
                  src="/Icons/admin-gif.gif"
                  alt="Loading animation"
                  style={{ width: "25px", height: "25px", marginLeft: "-20px" }}
                />
                <p className="heading-secondary">
                  Admin Portal&nbsp;&nbsp;/&nbsp;
                </p>
                {currentMenu.icon}
                <p className="dashboards-heading">{currentMenu.label}</p>
              </div>
            )}
          </div>

          <div className="pe-3 d-flex align-items-center">
            <DropDownProfile userType="admin" />
          </div>
        </Header>

        <div style={{ padding: "4px" }}>
          <Content
            style={{
              background: colorBgContainer,
              padding: 10,
              minHeight: "86vh",
              overflow: "auto",
            }}
          >
            <Outlet />
          </Content>
        </div>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
